<script>
import {saveAs} from 'file-saver';

import AddTrainingCategoryModal from "@/components/modals/AddTrainingCategoryModal.vue";
import DeleteWithConfirmation from "@/components/DeleteWithConfirmation.vue";
import PerPageSelect from "@/components/ui/PerPageSelect.vue";

export default {
    components: {DeleteWithConfirmation, AddTrainingCategoryModal, PerPageSelect},
    data() {
        return {
            loading: true,
            downloading: false,
            data: [],
            originalData: {},
            meta: {
                current_page: 1,
                total: 0,
                per_page: 0,
            },
            sort: {
                field: 'id',
                order: 'desc'
            },
            addCategoryModalOpened: false,
            controller: null,
            search: '',
            searchDebounceTimer: null,
            perPage: parseInt(localStorage.getItem('perPage_trainingCategories') || '10'),
        }
    },
    methods: {
        loadData() {
            this.controller = new AbortController();
            this.loading = true;
            window.scrollTo({top: 0});
            let params = {
                page: this.meta.current_page,
                per_page: this.perPage,
                sort: this.sort.field,
                order: this.sort.order,
                search: this.search || undefined,
            };
            axios.get('/training-categories', {
                signal: this.controller.signal,
                params: params
            }).then(response => {
                this.data = response.data.data;
                this.meta.current_page = response.data.meta.current_page;
                this.meta.total = response.data.meta.total;
                this.meta.per_page = response.data.meta.per_page;
            }).catch(() => {
            }).finally(() => {
                this.loading = false;
            })
        },
        onPageChange(page) {
            this.controller.abort();
            this.meta.current_page = page;
            this.loadData();
        },
        onSort(field, order) {
            this.controller.abort();
            this.sort = {field, order};
            this.loadData();
        },
        openAddCategoryModal() {
            this.addCategoryModalOpened = true;
        },
        closeAddCategoryModal(data) {
            if (data && data.saved) {
                this.$toast.success(this.$t('training_categories.messages.category_added'))
                this.loadData();
            }
            this.addCategoryModalOpened = false;
        },
        save(row) {
            row.saving = true;
            axios.put('/training-categories/' + row.id, {
                name: row.name,
            }).then(response => {
                this.$toast.success(this.$t('messages.updated_successfully'));
                row.status_label = response.data.data.status_label;
                row.status = response.data.data.status;
                row.name = response.data.data.name;
            })
                .catch(error => {
                    this.$toast.error(this.$t('messages.update_failed'))
                })
                .finally(() => {
                    row.editing = false;
                    row.saving = false;
                })
        },
        edit(row) {
            this.originalData[row.id] = {...row};
            row.editing = true;
        },
        cancelEditing(row) {
            row.status = this.originalData[row.id].status;
            row.name = this.originalData[row.id].name;
            row.editing = false;
        },
        onSearchInput() {
            clearTimeout(this.searchDebounceTimer);
            this.searchDebounceTimer = setTimeout(() => {
                this.controller && this.controller.abort();
                this.meta.current_page = 1;
                this.loadData();
            }, 400);
        },
        onPerPageChange() {
            localStorage.setItem('perPage_trainingCategories', String(this.perPage));
            this.meta.current_page = 1;
            this.loadData();
        },
        download() {
            this.downloading = true;
            let params = {
                sort: this.sort.field,
                order: this.sort.order,
                search: this.search || undefined,
                xlsx: 1
            }
            axios({
                url: '/training-categories',
                method: 'GET',
                responseType: 'blob',
                params
            }).then((response) => {
                saveAs(response.data, 'training-categories.xlsx');
            }).finally(() => {
                this.downloading = false;
            });
        }
    },
    mounted() {
        this.loadData();
    },
    beforeUnmount() {
        this.controller && this.controller.abort();
    }
}

</script>
<template>
    <section>

        <AddTrainingCategoryModal
            v-if="addCategoryModalOpened"
            @close="closeAddCategoryModal"/>

        <div class="buttons" v-if="$auth.isAdmin()">
            <b-button type="is-link" @click="openAddCategoryModal">
                {{ $t('training_categories.labels.add_category') }}
            </b-button>
            <b-button v-if="$auth.isAdmin()"
                      type="is-link"
                      icon-left="download"
                      :loading="downloading"
                      @click="download">
                .xlsx
            </b-button>
        </div>

        <b-field>
            <b-input
                v-model="search"
                @input="onSearchInput"
                :placeholder="$t('labels.search') + '...'"
                type="search"
                icon="magnify"
                clearable/>
        </b-field>

        <b-table
            :data="loading ? [] : data"
            :loading="loading"

            :paginated="!loading"
            backend-pagination
            :total="loading ? 0 : meta.total"
            :per-page="perPage"
            @page-change="onPageChange"
            :aria-next-label="$t('navigation.next_page')"
            :aria-previous-label="$t('navigation.previous_page')"
            :aria-page-label="$t('navigation.page')"
            :aria-current-label="$t('navigation.current_page')"

            backend-sorting
            sort-icon="arrow-up"
            default-sort-direction="asc"
            :default-sort="['id', 'desc']"
            @sort="onSort">

            <template #empty>
                <div v-if="!loading" class="block has-text-grey has-text-centered mb-4 mt-4">
                    {{ $t('training_categories.labels.no_categories') }}
                </div>
            </template>

            <b-table-column field="id" label="ID" v-slot="props" sortable width="50px">
                {{ props.row.id }}
            </b-table-column>

            <b-table-column field="name" :label="$t('training_categories.fields.name.name')" sortable v-slot="props">
                <span v-if="!props.row.editing">{{ props.row.name }}</span>
                <b-input type="text" v-if="props.row.editing" v-model="props.row.name"
                         :disabled="props.row.saving"/>
            </b-table-column>

            <b-table-column field="created_at" :label="$t('training_categories.fields.created_at.name')" v-slot="props" sortable width="150px">
                {{ props.row.created_at_diff }}
            </b-table-column>


            <b-table-column field="" label="-" v-slot="props" width="120px">

                <section class="buttons">

                    <b-button type="is-link" size="is-small"
                              :title="$t('actions.edit')"
                              @click="edit(props.row)"
                              icon-right="square-edit-outline"
                              v-if="!props.row.editing">
                    </b-button>

                    <b-button type="is-success" size="is-small"
                              :title="$t('actions.save')"
                              v-bind:disabled="props.row.saving"
                              @click="save(props.row)"
                              v-bind:class="{'is-loading': props.row.saving}"
                              icon-right="check"
                              v-if="props.row.editing">
                    </b-button>

                    <b-button size="is-small"
                              :title="$t('actions.cancel')"
                              v-bind:disabled="props.row.saving"
                              v-bind:class="{'is-loading': props.row.saving}"
                              @click="cancelEditing(props.row)"
                              icon-right="close"
                              v-if="props.row.editing">
                    </b-button>

                    <DeleteWithConfirmation
                        v-on:deleted="loadData"
                        small icon
                        :content="$t('training_categories.messages.removal_confirmation')"
                        button-title="Usuń kategorię"
                        :url="'/training-categories/' + props.row.id"/>

                </section>

            </b-table-column>

            <template #bottom-left>
                <PerPageSelect v-model="perPage" @change="onPerPageChange" />
            </template>

        </b-table>

        <loader :loading="loading"></loader>
    </section>
</template>
